import { blocksForDate, eventsForDate, timeToMinutes } from "./plannerItems";
import { isGhostEvent } from "./lumenCalendar";

export const SNAP_MINUTES = 15;
export const MIN_BLOCK_MINUTES = 15;
export const DAY_START = "06:00";

export const minutesToTime = (mins) => {
  const capped = Math.max(0, Math.min(24 * 60 - 1, Math.round(mins)));
  return `${String(Math.floor(capped / 60)).padStart(2, "0")}:${String(capped % 60).padStart(2, "0")}`;
};

export const snap = (mins) => Math.round(mins / SNAP_MINUTES) * SNAP_MINUTES;

export function nextDateKey(dateKey) {
  const [y, m, d] = dateKey.split("-").map(Number);
  const next = new Date(y, m - 1, d + 1);
  return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}-${String(next.getDate()).padStart(2, "0")}`;
}

export const busyEventSlots = (items, dateKey) =>
  eventsForDate(items, dateKey).filter((e) => !isGhostEvent(e)).map((e) => ({
    from: timeToMinutes(e.start_time),
    to: timeToMinutes(e.end_time),
  }));

/** First minute at or after `from` where `length` minutes are free of events. */
export function firstFreeStart(from, length, events) {
  let start = from;
  let moved = true;
  while (moved) {
    moved = false;
    for (const slot of events) {
      if (start < slot.to && start + length > slot.from) {
        start = slot.to;
        moved = true;
      }
    }
  }
  return start;
}

/**
 * The longest a block can run from where it starts: up to the next calendar event,
 * or the end of the day, whichever comes first.
 */
export function maxLengthFor(items, dateKey, block, dayEndTime) {
  const start = timeToMinutes(block.start_time);
  const nextEvent = busyEventSlots(items, dateKey)
    .filter((slot) => slot.from >= start)
    .sort((a, b) => a.from - b.from)[0];
  const limit = Math.min(nextEvent ? nextEvent.from : Infinity, timeToMinutes(dayEndTime));
  return Math.max(MIN_BLOCK_MINUTES, limit - start);
}

const endOf = (block) => timeToMinutes(block.start_time) + block.duration_minutes;

/**
 * Applies a change to the day the way a person would expect it to ripple:
 *
 * - The block being resized keeps its start; its end stops at the next event or the
 *   end of the day.
 * - Only the blocks directly after it move: ones it now runs into are pushed later,
 *   and ones that sat right behind it are pulled up when it gets shorter. The first
 *   gap stops the ripple, so anything placed with free time around it stays put.
 * - Calendar events never move; blocks step over them.
 * - With no block being resized (the end of the day moved), nothing slides; blocks
 *   that now run past the end of the day are handed back to roll over.
 */
export function layOutDay(items, dateKey, dayEndTime, overrides = {}, protectedId = null) {
  const events = busyEventSlots(items, dateKey);
  const dayEnd = timeToMinutes(dayEndTime);
  const originals = blocksForDate(items, dateKey);
  const lengthOf = (b) => overrides[b.id]?.duration_minutes ?? b.duration_minutes;

  // A chain continues across an event that begins exactly where the chain ended.
  const pastEvents = (at) => {
    let t = at;
    let moved = true;
    while (moved) {
      moved = false;
      for (const slot of events) {
        if (slot.from <= t && slot.to > t) { t = slot.to; moved = true; }
      }
    }
    return t;
  };

  const positions = new Map();
  const overflow = [];
  const held = originals.find((b) => b.id === protectedId);

  if (held) {
    const heldStart = timeToMinutes(held.start_time);
    const heldLength = Math.min(lengthOf(held), maxLengthFor(items, dateKey, held, dayEndTime));
    positions.set(held.id, { start: heldStart, length: heldLength });

    const placed = originals
      .filter((b) => b.id !== held.id && timeToMinutes(b.start_time) < heldStart)
      .map((b) => ({ from: timeToMinutes(b.start_time), to: endOf(b) }));
    placed.push({ from: heldStart, to: heldStart + heldLength });

    let oldCursor = pastEvents(endOf(held));
    let newCursor = heldStart + heldLength;

    const after = originals.filter((b) => b.id !== held.id && timeToMinutes(b.start_time) >= heldStart);
    for (const block of after) {
      const start = timeToMinutes(block.start_time);
      const length = lengthOf(block);
      const wasRightBehind = start <= oldCursor;
      const isInTheWay = start < newCursor;
      if (!wasRightBehind && !isInTheWay) break;

      oldCursor = pastEvents(Math.max(oldCursor, endOf(block)));
      const newStart = firstFreeStart(newCursor, length, [...events, ...placed]);
      if (newStart + length > dayEnd) {
        overflow.push(block);
        continue;
      }
      placed.push({ from: newStart, to: newStart + length });
      positions.set(block.id, { start: newStart, length });
      newCursor = newStart + length;
    }
  } else {
    for (const block of originals) {
      if (endOf(block) > dayEnd) overflow.push(block);
    }
  }

  const changed = [];
  for (const original of originals) {
    const spot = positions.get(original.id);
    if (!spot) continue;
    const startTime = minutesToTime(spot.start);
    if (startTime !== original.start_time || spot.length !== original.duration_minutes) {
      changed.push({ id: original.id, start_time: startTime, duration_minutes: spot.length });
    }
  }

  return { changed, overflow };
}

/** Applies block patches to a copy of the saved items, to see the day as it will be. */
export function withPatches(items, patches) {
  const byId = new Map(patches.map((p) => [p.id, p]));
  return items.map((i) => (byId.has(i.id) ? { ...i, ...byId.get(i.id) } : i));
}

/**
 * Where a block that was sent to tomorrow can go back today: as close to where it was
 * as the day now allows, without touching an event or another block. Null if it
 * still does not fit before the end of the day.
 */
export function returnSlot(items, dateKey, block, preferredStart, dayEndTime) {
  const busy = [
    ...busyEventSlots(items, dateKey),
    ...blocksForDate(items, dateKey)
      .filter((b) => b.id !== block.id)
      .map((b) => ({ from: timeToMinutes(b.start_time), to: endOf(b) })),
  ];
  const start = firstFreeStart(timeToMinutes(preferredStart), block.duration_minutes, busy);
  return start + block.duration_minutes <= timeToMinutes(dayEndTime) ? minutesToTime(start) : null;
}

/** Where a block rolled to another day should start: after whatever is already there. */
export function rollOverStart(items, targetDateKey, length, dayEndTime) {
  const events = busyEventSlots(items, targetDateKey);
  const existing = blocksForDate(items, targetDateKey);
  const last = existing.length
    ? Math.max(...existing.map((b) => timeToMinutes(b.start_time) + b.duration_minutes))
    : timeToMinutes(DAY_START);
  const start = firstFreeStart(last, length, events);
  return start + length <= timeToMinutes(dayEndTime) ? minutesToTime(start) : minutesToTime(last);
}

/** Unfinished blocks from days gone by, so they can be carried to today. */
export function staleBlocks(items, todayKey) {
  return (items || [])
    .filter((i) => i.kind === "day_block" && !i.proposal && !i.done && i.date < todayKey)
    .sort((a, b) => `${a.date}${a.start_time}`.localeCompare(`${b.date}${b.start_time}`));
}
